import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import styles from './NotificationBell.module.css';
import BellIcon from '../../../../public/assets/icons/bell.svg?react';
import {
  NOTIFICATIONS_CHANGED_EVENT,
  NotificationApi,
  NotificationItem,
} from '@/services/notification.service';

import { tr, appLocale } from '@/i18n';
import { htmlToText } from '@/utils/sanitize';
function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return tr("widgets.latest_feedback_just_now", "just now");
  const mins = Math.floor(secs / 60);
  if (mins < 60) return tr("ui.xm_ago", "{v0}m ago", { v0: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return tr("ui.xh_ago", "{v0}h ago", { v0: hrs });
  const days = Math.floor(hrs / 24);
  if (days < 7) return tr("ui.xd_ago", "{v0}d ago", { v0: days });
  return new Date(iso).toLocaleDateString(appLocale());
}

function stripHtml(html: string | null): string {
  if (!html) return '';
  // Block boundaries become spaces, otherwise "<p>Hi,</p><p>You…</p>" reads "Hi,You…" (DEF-NT-003).
  return htmlToText(html.replace(/<\/(p|div|li|h[1-6]|tr|blockquote)\s*>|<br\s*\/?>/gi, '$& ')).replace(/\s+/g, ' ').trim();
}

const UNREAD_KEY = ['notifications-unread'] as const;

export const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Unread count, polled so the badge stays fresh. A React Query entry, so a header that re-mounts (e.g. when
  // updated translations arrive) reuses it instead of asking again (dashboard audit 2026-11-02).
  const qc = useQueryClient();
  const { data: unreadData } = useQuery({
    queryKey: UNREAD_KEY,
    queryFn: () => NotificationApi.unreadCount(),
    refetchInterval: 60000,
    retry: false,
  });
  const unread = unreadData ?? 0;
  const setUnread = useCallback(
    (v: number | ((n: number) => number)) =>
      qc.setQueryData<number>(UNREAD_KEY, (old) => (typeof v === 'function' ? v(old ?? 0) : v)),
    [qc],
  );
  const refreshCount = useCallback(() => {
    void qc.invalidateQueries({ queryKey: UNREAD_KEY });
  }, [qc]);

  // Refresh immediately when a read-state change happens elsewhere (e.g. the
  // notifications page), so the badge never goes stale.
  useEffect(() => {
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, refreshCount);
    return () => window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, refreshCount);
  }, [refreshCount]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await NotificationApi.list({ limit: 8 });
      setItems(res.items);
      setUnread(res.unread_count);
    } catch {
      /* non-fatal */
    } finally {
      setLoading(false);
    }
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadList();
  };

  const handleClickItem = async (n: NotificationItem) => {
    if (!n.is_read) {
      try {
        await NotificationApi.markRead(n.id);
      } catch {
        /* non-fatal */
      }
      setItems(prev => prev.map(x => (x.id === n.id ? { ...x, is_read: true } : x)));
      setUnread(c => Math.max(0, c - 1));
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const handleMarkAll = async () => {
    try {
      await NotificationApi.markAllRead();
    } catch {
      /* non-fatal */
    }
    setItems(prev => prev.map(x => ({ ...x, is_read: true })));
    setUnread(0);
  };

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        className={styles.bellButton}
        title={tr("ui.notifications", "Notifications")}
        aria-label={tr("ui.notifications", "Notifications")}
        onClick={toggle}
      >
        <BellIcon aria-hidden="true" />
        {unread > 0 && <span className={styles.badge}>{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div className={styles.panel}>
          <div className={styles.panelHeader}>
            <span className={styles.panelTitle}>{tr("ui.notifications", "Notifications")}</span>
            {unread > 0 && (
              <button type="button" className={styles.markAll} onClick={handleMarkAll}>
                {tr("ui.mark_all_read", "Mark all read")}
              </button>
            )}
          </div>

          <div className={styles.list}>
            {loading && <div className={styles.empty}>{tr("common.loading", "Loading…")}</div>}
            {!loading && items.length === 0 && (
              <div className={styles.empty}>{tr("ui.you_re_all_caught_up", "You're all caught up 🎉")}</div>
            )}
            {!loading &&
              items.map(n => (
                <button
                  key={n.id}
                  type="button"
                  className={`${styles.item} ${n.is_read ? '' : styles.itemUnread}`}
                  onClick={() => handleClickItem(n)}
                >
                  <span className={styles.dotCol}>
                    {!n.is_read && <span className={styles.dot} />}
                  </span>
                  <span className={styles.itemBody}>
                    <span className={styles.itemTitle}>{n.title || n.type}</span>
                    {n.body && <span className={styles.itemText}>{stripHtml(n.body)}</span>}
                    <span className={styles.itemTime}>{timeAgo(n.created_at)}</span>
                  </span>
                </button>
              ))}
          </div>

          <button
            type="button"
            className={styles.viewAll}
            onClick={() => {
              setOpen(false);
              navigate('/user/notifications');
            }}
          >
            {tr("ui.view_all_notifications", "View all notifications")}
          </button>
        </div>
      )}
    </div>
  );
};
